// React and related imports
import React, { useEffect, useRef } from 'react';

// Flowbite components
import { Button } from 'flowbite-react';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Components
import { LogoImage } from '../components/Logo/LogoImage';
import { LogoText } from '../components/Logo/LogoText';

// Hooks
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Public page shown after signing out. It never redirects on its own: sending a signed-out browser
 * straight to Google would sign it back in while the Google session lasts.
 */
export const SignedOutPage: React.FC = () => {
  const { t } = useTranslation();
  const signInRef = useRef<HTMLButtonElement>(null);

  usePageTitle(t('pageTitles.signedOut'));

  useEffect(() => {
    signInRef.current?.focus();
  }, []);

  const signIn = () =>
    window.location.assign(
      `${process.env.REACT_APP_BACKEND_URL}/dashboard-auth/google`
    );

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center">
          <LogoImage />
          <LogoText />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('signedOut.title')}
        </h1>
        <p className="text-base text-gray-600 dark:text-gray-300">
          {t('signedOut.body')}
        </p>
        <Button
          ref={signInRef}
          gradientMonochrome="success"
          className="w-full"
          onClick={signIn}
        >
          {t('signedOut.signIn')}
        </Button>
      </div>
    </main>
  );
};
